import React, { useState, useEffect } from 'react'
import { isAnyElementInArray } from '../../utils/arrayUtil'
import GpsTrackersListItem from './GpsTrackersListItem'
import DetailGpsTrackerModal from './DetailGpsTrackerModal'
import './OverviewItemList.css'
import { getGroupGpsTrackers } from '../../modules/GpsTracker'

type Props = {
  group: IGroup
}

const GPS_TRACKER_INFO_ACQUIRED_HELP = '電池残量などの端末情報を取得できた時刻です。'
const GPS_TRACKER_HEARTBEAT_RECEIVED_HELP = 'サーバーが端末からの定期的な通信を受信した時刻です。'
const GPS_TRACKER_LAST_LOCATION_HELP = '端末の振動や移動により、位置情報を更新した時刻です'
const GPS_TRACKER_WIFI_LOCATION_HELP = '端末が周辺のWi-Fi情報を取得し、位置を推定した時刻です。'
const GPS_TRACKER_CELLULAR_LOCATION_HELP = '端末が携帯基地局の情報を取得した時刻です。'
const GPS_TRACKER_MANUAL_PATH = `${process.env.PUBLIC_URL || ''}/GPS_Manual.pdf`

type HeaderTitleWithHelpProps = {
  label: string
  helpText: string
  tooltipId: string
}

const HeaderTitleWithHelp = ({
  label,
  helpText,
  tooltipId,
}: HeaderTitleWithHelpProps) => (
  <div className="title overview-list-header-title-with-help">
    <span>{label}</span>
    <span className="overview-list-header-help">
      <button
        type="button"
        className="overview-list-header-help__trigger"
        aria-label={`${label}の説明`}
        aria-describedby={tooltipId}
      >
        ?
      </button>
      <span
        id={tooltipId}
        className="overview-list-header-help__tooltip"
        role="tooltip"
      >
        {helpText}
      </span>
    </span>
  </div>
)

const GpsTrackersList = ({
  group,
}: Props) => {
  const [gpsTrackers, setGpsTrackers] = useState<IGpsTracker[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedTracker, setSelectedTracker] = useState<IGpsTracker | null>(null)

  const closeModal = () => {
    setSelectedTracker(null)
  }

  useEffect(() => {
    const fetchGpsTrackers = async () => {
      try {
        setLoading(true)
        const trackers = await getGroupGpsTrackers(group.id)
        setGpsTrackers(trackers)
        setError(null)
      } catch (err) {
        setError('GPSトラッカーの取得に失敗しました')
        console.error('Failed to fetch GPS trackers:', err)
      } finally {
        setLoading(false)
      }
    }

    fetchGpsTrackers()
  }, [group.id])

  const emptyItem = () => (
    <tr>
      <td colSpan={8} className="list-row">
        {loading ? 'GPSトラッカーを読み込み中...' : 'このグループにはGPSトラッカーがありません。'}
      </td>
    </tr>
  )

  if (error) {
    return (
      <div className="overview-list">
        <div className="error-message">{error}</div>
      </div>
    )
  }

  return (
    <div className="overview-list gps-trackers-list" data-testid="gps-trackers-list">
      <div className="gps-tracker-manual-actions">
        <a
          className="gps-tracker-manual-link"
          href={GPS_TRACKER_MANUAL_PATH}
          target="_blank"
          rel="noopener noreferrer"
        >
          GPSトラッカーマニュアル
        </a>
      </div>
      <table className="table">
        <thead className="header" data-testid="gps-trackers-list-header">
          <tr>
            <th>
              <div className="title">
                端末の名前
              </div>
            </th>
            <th>
              <HeaderTitleWithHelp
                label="定期通信"
                helpText={GPS_TRACKER_HEARTBEAT_RECEIVED_HELP}
                tooltipId="gps-tracker-heartbeat-received-help"
              />
            </th>
            <th>
              <HeaderTitleWithHelp
                label="端末の情報取得"
                helpText={GPS_TRACKER_INFO_ACQUIRED_HELP}
                tooltipId="gps-tracker-info-acquired-help"
              />
            </th>
            <th>
              <HeaderTitleWithHelp
                label="最終GPS位置更新時刻"
                helpText={GPS_TRACKER_LAST_LOCATION_HELP}
                tooltipId="gps-tracker-last-location-help"
              />
            </th>
            <th>
              <HeaderTitleWithHelp
                label="Wi-Fiによる位置更新時刻"
                helpText={GPS_TRACKER_WIFI_LOCATION_HELP}
                tooltipId="gps-tracker-wifi-location-help"
              />
            </th>
            <th>
              <HeaderTitleWithHelp
                label="携帯基地局による位置更新時刻"
                helpText={GPS_TRACKER_CELLULAR_LOCATION_HELP}
                tooltipId="gps-tracker-cellular-location-help"
              />
            </th>
            <th>
              <div className="title">
                所持者
              </div>
            </th>
            <th>
              <div className="title">
                バッテリー残量
              </div>
            </th>
          </tr>
        </thead>
        <tbody className="list" data-testid="gps-trackers-list-body">
          {
            !gpsTrackers || !isAnyElementInArray(gpsTrackers)
              ? emptyItem()
              : gpsTrackers.map((tracker: IGpsTracker) => (
                <GpsTrackersListItem
                  key={tracker.device_token}
                  tracker={tracker}
                  onClickList={() => setSelectedTracker(tracker)}
                />
              ))
          }
        </tbody>
      </table>
      {selectedTracker && (
        <DetailGpsTrackerModal
          modalProps={{
            tracker: selectedTracker,
            group,
          }}
          closeModalFunction={closeModal}
        />
      )}
    </div>
  )
}

export default GpsTrackersList
